/* ============================================================================
   Slide 6 — "The Sleep Cycle" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide6Inner (desktop) and HTLDSlide6Mobile. Edit copy here
   — English and German side by side; the German object must have exactly the
   same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent6 = "#38b5b5";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface SleepStage { stage: string; type: string; duration: string; rem: boolean; desc: string; }

const en = {
  meta: {
    breadcrumbPre: "Sleep Architecture",
    breadcrumbAccent: "When dreams happen",
    titlePlain: "The Sleep",
    titleAccent: "Cycle",
    subtitle: "Sleep is not a single state — it is a structured cycle that repeats through the night",
    stagesLabel: "The four stages",
    calloutLabel: "Why Dreamfulness targets REM",
  },
  stages: [
    { stage: "Stage 1", type: "Light NREM", duration: "1–7 min",   rem: false, desc: "Transition into sleep. Muscles relax, awareness fades." },
    { stage: "Stage 2", type: "NREM",       duration: "10–25 min", rem: false, desc: "Heart rate slows, body temperature drops. Sleep deepens." },
    { stage: "Stage 3", type: "Deep NREM",  duration: "20–40 min", rem: false, desc: "Slow-wave sleep. Physical restoration, immune repair." },
    { stage: "REM",     type: "REM Sleep",  duration: "10–60 min", rem: true,  desc: "Eyes move rapidly. Brain nearly as active as waking. Dreams." },
  ] as SleepStage[],
  callout: [
    { t: "REM is the only state where the subconscious is fully active while the body rests. The brain is nearly as active as waking — but the logical mind is quieted. " },
    { t: "This is the window Dreamfulness is designed to enter.", b: true },
  ] as Seg[],
  canvas: {
    /** Y-axis labels, top to bottom: awake, REM, light, deep. */
    stageLabels: ["AWAKE", "REM", "LIGHT", "DEEP"],
    title: "SLEEP ARCHITECTURE · ONE NIGHT",
    timeAxis: "TIME (HOURS)",
    cycle: "Cycle",
    distTitle: "STAGE DISTRIBUTION  (avg. adult, 8h night)",
    /** Distribution bar names: awake, light, deep, REM. */
    distNames: ["AWAKE", "LIGHT", "DEEP", "REM"],
    insightsTitle: "STAGE INSIGHTS",
    /** Insight rows: deep, REM, light. The REM tag must contain "REM". */
    insights: [
      { tag: "DEEP",  text: "NREM · growth hormone release · tissue repair · immune strengthening" },
      { tag: "REM ◀", text: "Emotional processing · memory consolidation · Dreamfulness works here" },
      { tag: "LIGHT", text: "NREM · sleep spindles · transitions between stages · fragile sleep" },
    ],
  },
};

export type Slide6Content = typeof en;

const de: Slide6Content = {
  meta: {
    breadcrumbPre: "Schlafarchitektur",
    breadcrumbAccent: "Wann wir träumen",
    titlePlain: "Der",
    titleAccent: "Schlafzyklus",
    subtitle: "Schlaf ist kein einheitlicher Zustand – sondern ein Zyklus, der sich die Nacht über wiederholt",
    stagesLabel: "Die vier Phasen",
    calloutLabel: "Warum Dreamfulness beim REM-Schlaf ansetzt",
  },
  stages: [
    { stage: "Phase 1", type: "Leichter NREM-Schlaf", duration: "1–7 min",   rem: false, desc: "Übergang in den Schlaf. Die Muskeln entspannen sich, das Bewusstsein schwindet." },
    { stage: "Phase 2", type: "NREM-Schlaf",          duration: "10–25 min", rem: false, desc: "Der Puls wird langsamer, die Körpertemperatur sinkt. Der Schlaf vertieft sich." },
    { stage: "Phase 3", type: "Tiefer NREM-Schlaf",   duration: "20–40 min", rem: false, desc: "Tiefschlaf mit langsamen Wellen. Körperliche Erholung, Stärkung der Abwehr." },
    { stage: "REM",     type: "REM-Schlaf",           duration: "10–60 min", rem: true,  desc: "Schnelle Augenbewegungen. Das Gehirn ist fast so aktiv wie im Wachzustand. Träume." },
  ],
  callout: [
    { t: "REM ist der einzige Zustand, in dem das Unterbewusstsein voll aktiv ist, während der Körper ruht. Das Gehirn ist fast so aktiv wie im Wachsein – doch der logische Verstand schweigt. " },
    { t: "Genau dieses Fenster nutzt Dreamfulness.", b: true },
  ],
  canvas: {
    stageLabels: ["WACH", "REM", "LEICHT", "TIEF"],
    title: "SCHLAFARCHITEKTUR · EINE NACHT",
    timeAxis: "ZEIT (STUNDEN)",
    cycle: "Zyklus",
    distTitle: "PHASENVERTEILUNG  (Ø Erwachsene, 8 h)",
    distNames: ["WACH", "LEICHT", "TIEF", "REM"],
    insightsTitle: "DIE PHASEN IM ÜBERBLICK",
    insights: [
      { tag: "TIEF",  text: "NREM · Wachstumshormone · Gewebereparatur · Immunstärkung" },
      { tag: "REM ◀", text: "Gefühls- & Gedächtnisverarbeitung · hier wirkt Dreamfulness" },
      { tag: "LEICHT", text: "NREM · Schlafspindeln · Phasenübergänge · leicht störbar" },
    ],
  },
};

export const slide6Content: ByLang<Slide6Content> = { en, de };
export const useSlide6 = () => useL(slide6Content);
